/**
 * Ask Aelo loader: the only Ask Aelo code that runs on page load (budget: under 3 KB gzipped).
 *
 * - Shows the chat to every visitor. The 50/50 test ended on 2026-10-07 (owner: one experiment at a time, and right
 *   now that is the ads), so the old per-browser assignment is cleared and ?ask= no longer changes anything.
 * - Shows the launcher after about 8 seconds or the first scroll, clear of the email forms and the privacy notice, and
 *   hidden while a page field has the keyboard open.
 * - Loads the panel code and the knowledge base only on first tap (the build wiring passes their addresses).
 */
import type { AskPanelApi, AskCapture } from './types'

const RETIRED_VARIANT_KEY = 'aelo_ask_variant_v1'
const JOINED_KEY = 'aelo_ask_joined_v1'
const HANDOFF_KEY = 'aelo_ask_join_handoff_v1'
const SEEN_KEY = 'aelo_ask_seen_v1'

// The launcher: an ink button with the Aelo mark. It rises in once, lifts under a mouse, presses in on tap, and sinks
// away while the panel opens from the same corner.
const LAUNCHER_CSS = `:host{all:initial}
.aa-launcher{position:fixed;right:20px;bottom:var(--aa-bottom,20px);z-index:60;display:inline-flex;align-items:center;gap:10px;min-height:48px;padding:6px 18px 6px 6px;border:0;border-radius:0;background:#092e3e;color:#fff;font:600 14px/1.2 Manrope,'Avenir Next',system-ui,sans-serif;letter-spacing:-.01em;cursor:pointer;-webkit-tap-highlight-color:transparent;box-shadow:0 1px 2px rgba(9,46,62,.16),0 14px 32px -10px rgba(9,46,62,.5);opacity:0;transform:translateY(12px) scale(.96);transition:opacity .16s ease-out,transform .16s ease-out,background-color .15s,visibility 0s linear .16s;visibility:hidden}
.aa-launcher.aa-in{opacity:1;transform:none;visibility:visible;transition:opacity .3s ease-out,transform .4s cubic-bezier(.23,1,.32,1),background-color .15s}
.aa-m{display:grid;place-items:center;box-sizing:border-box;width:36px;height:36px;padding-bottom:3px;border-radius:50%;background:#f8f5ef;color:#092e3e;font-size:20px;font-weight:800;letter-spacing:0}
.aa-q{margin-right:-5px;font-weight:500;opacity:.78}
.aa-launcher:focus-visible{outline:2px solid #092e3e;outline-offset:3px;box-shadow:0 0 0 2px #fff}
.aa-launcher[aria-busy=true]{cursor:progress;opacity:.8}
.aa-launcher.aa-in:active{transform:scale(.96)}
@media(hover:hover) and (pointer:fine){.aa-launcher.aa-in:hover{background:#164b60;transform:translateY(-2px)}}
@media(max-width:640px){.aa-launcher{right:14px;padding-right:16px}.aa-q{display:none}}
@media(prefers-reduced-motion:reduce){.aa-launcher,.aa-launcher.aa-in{transition:none;transform:none!important}}`

function storage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try { return window[kind] } catch { return null }
}
function read(kind: 'localStorage' | 'sessionStorage', key: string): string | null {
  try { return storage(kind)?.getItem(key) ?? null } catch { return null }
}
function write(kind: 'localStorage' | 'sessionStorage', key: string, value: string | null) {
  try { const s = storage(kind); if (!s) return; if (value === null) s.removeItem(key); else s.setItem(key, value) } catch { /* Storage can be blocked. */ }
}

function start() {
  const script = document.querySelector<HTMLScriptElement>('script[data-aelo-ask-aelo]')
  const panelUrl = script?.dataset.panel
  const faqUrl = script?.dataset.faq
  if (!panelUrl || !faqUrl || document.getElementById('aelo-ask-aelo')) return
  if (/^\/(admin|founding\/unsubscribe|privacy|terms|launch\/(privacy|terms)\.html)(\/|$)/.test(location.pathname)) return

  // Everyone gets the chat. "on" stays on events, contact requests and the question log so older and newer rows read
  // the same way.
  const variant = 'on' as const
  write('localStorage', RETIRED_VARIANT_KEY, null)
  const articleSlug = document.body.dataset.aeloArticleSlug
  const capture: AskCapture = (event, properties = {}) => {
    try {
      window.AeloPrelaunchAnalytics?.capture(event, {
        variant: `ask-${variant}`, path: location.pathname, ...(articleSlug ? { article_slug: articleSlug } : {}), ...properties,
      })
    } catch { /* Analytics never blocks the page. */ }
  }
  // Signups from any form in this tab let the panel switch from "join" to "share".
  window.addEventListener('aelo:website-signup', event => {
    if ((event as CustomEvent<{ stage?: string }>).detail?.stage === 'accepted') write('sessionStorage', JOINED_KEY, '1')
  })
  const handoff = read('sessionStorage', HANDOFF_KEY)

  const host = document.createElement('div')
  host.id = 'aelo-ask-aelo'
  host.className = 'ph-no-capture'
  host.setAttribute('data-private', '')
  const shadow = host.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = LAUNCHER_CSS
  const launcher = document.createElement('button')
  launcher.type = 'button'
  launcher.className = 'aa-launcher'
  launcher.setAttribute('aria-haspopup', 'dialog')
  launcher.setAttribute('aria-expanded', 'false')
  launcher.innerHTML = '<span class="aa-m" aria-hidden="true">a</span><span class="aa-q">Questions?</span><span class="aa-lbl">Ask Aelo</span>'
  launcher.setAttribute('aria-label', 'Questions? Ask Aelo')
  shadow.append(style, launcher)
  document.body.append(host)

  let panel: Promise<AskPanelApi> | null = null
  function loadPanel(): Promise<AskPanelApi> {
    panel ??= import(/* @vite-ignore */ panelUrl!).then(() => {
      const registered = window.AeloAskAeloPanel
      if (!registered) throw new Error('Ask Aelo panel missing')
      return registered.mount({ host, shadow, launcher, faqUrl: faqUrl!, variant, capture, onToggle: () => place() })
    }).catch(error => { panel = null; throw error })
    return panel
  }
  async function open(source: 'launcher' | 'handoff') {
    launcher.setAttribute('aria-busy', 'true')
    try {
      const api = await loadPanel()
      api.open(source)
    } catch {
      launcher.removeAttribute('aria-busy')
      launcher.querySelector('.aa-lbl')!.textContent = 'Ask Aelo (try again)'
      return
    }
    launcher.removeAttribute('aria-busy')
  }
  launcher.addEventListener('click', () => { void open('launcher') })
  // Warm the panel on intent so the first tap feels instant.
  for (const type of ['pointerenter', 'focus', 'touchstart']) launcher.addEventListener(type, () => { void loadPanel().catch(() => {}) }, { once: true, passive: true })

  // ---- placement: late, out of the way of forms, the privacy notice and the keyboard
  let shown = false
  let keyboard = false
  function show() {
    if (shown) return
    shown = true
    write('sessionStorage', SEEN_KEY, '1')
    capture('aelo_ask_shown')
    place()
  }
  function overlaps(a: DOMRect, b: DOMRect, margin = 10) {
    return a.width > 0 && b.width > 0 && a.left < b.right + margin && a.right > b.left - margin && a.top < b.bottom + margin && a.bottom > b.top - margin
  }
  function place() {
    const notice = document.querySelector('.aelo-privacy-panel')
    const noticeRect = notice?.getBoundingClientRect()
    const noticeHeight = noticeRect && noticeRect.height && noticeRect.top < innerHeight && noticeRect.right > innerWidth - 240
      ? Math.round(innerHeight - noticeRect.top) : 0
    // The permanent privacy controls (bottom-left) reach under the launcher on phones; the launcher sits just above them.
    const controlsRect = document.querySelector('.aelo-privacy-settings')?.getBoundingClientRect()
    const controlsHeight = controlsRect && controlsRect.height && controlsRect.top < innerHeight && controlsRect.right > innerWidth - 240
      ? Math.round(innerHeight - controlsRect.top) : 0
    const lift = Math.max(noticeHeight, controlsHeight)
    host.style.setProperty('--aa-bottom', `${lift ? lift + 12 : 20}px`)
    // The phone sheet keeps clear of the privacy notice and of the on-screen keyboard, whichever is taller.
    const vv = window.visualViewport
    const keyboardHeight = vv ? Math.max(0, Math.round(innerHeight - vv.height - vv.offsetTop)) : 0
    const clear = Math.max(noticeHeight, keyboardHeight)
    host.style.setProperty('--aa-sheet-bottom', `${clear}px`)
    host.style.setProperty('--aa-sheet-max', `${Math.max(240, innerHeight - clear - 12)}px`)
    const rect = launcher.getBoundingClientRect()
    let blocked = keyboard
    if (!blocked && shown) {
      for (const form of document.querySelectorAll('form.signup-form, #signin-form')) {
        if (overlaps(rect, form.getBoundingClientRect())) { blocked = true; break }
      }
    }
    const open = launcher.getAttribute('aria-expanded') === 'true'
    launcher.classList.toggle('aa-in', shown && !blocked && !open)
    launcher.toggleAttribute('inert', !shown || blocked || open)
  }
  let queued = false
  const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; place() }) }
  addEventListener('scroll', () => { if (!shown && scrollY > 40) show(); schedule() }, { passive: true })
  addEventListener('resize', schedule, { passive: true })
  window.visualViewport?.addEventListener('resize', schedule)
  // The privacy notice mounts and unmounts deep inside the page.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true })
  if (matchMedia('(pointer: coarse)').matches) {
    const field = (target: EventTarget | null) => target instanceof HTMLElement && target.matches('input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]),textarea,select')
    document.addEventListener('focusin', event => { if (field(event.target)) { keyboard = true; schedule() } })
    document.addEventListener('focusout', event => { if (field(event.target)) { keyboard = false; setTimeout(schedule, 250) } })
  }
  if (read('sessionStorage', SEEN_KEY)) show()
  else setTimeout(show, 8000)

  window.AeloAskAelo = { variant, open: () => open('launcher') }
  // A join started on a page without a signup form finishes here, on the homepage form.
  if (handoff) {
    write('sessionStorage', HANDOFF_KEY, null)
    try {
      const data = JSON.parse(handoff) as { email?: string; at?: number; source?: string }
      if (data.email && typeof data.at === 'number' && Date.now() - data.at < 300_000 && document.querySelector('form.signup-form')) {
        show()
        void loadPanel().then(api => api.resumeJoin(data.email!, data.source === 'contact' ? 'contact' : 'answer')).catch(() => {})
      }
    } catch { /* A broken hand-off is dropped. */ }
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true })
else start()
